'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left text-xl font-semibold',
              layer === 1 && 'text-[16px] text-lg font-medium',
              layer && layer > 1 && 'text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const translatedMenuLabel = t('text3241').trim();
  const menuLabel =
    translatedMenuLabel && translatedMenuLabel !== 'text3241' ? translatedMenuLabel : 'Menu';

  return (
    <>
      <Sheet>
        <SheetTrigger
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
          aria-label={menuLabel}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          side={props.side}
          className={cn(
            'top-[97px] justify-between overflow-hidden bg-white/75 pt-12',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
        >
          {!!props.menu && (
            <div className="flex w-full flex-col items-center justify-center gap-12 p-4">
              {props.menu?.map((item, index) => {
                const Comp = item.path
                  ? isRelativeHref(getSafeHref(item.path))
                    ? Link
                    : 'a'
                  : 'span';
                return (
                  <SheetClose key={index} asChild>
                    <Comp
                      href={item.path ? getSafeHref(item.path) : (undefined as any)}
                      className="flex w-full items-center justify-center text-center"
                      prefetch={false}
                    >
                      <div className={cn('text-base font-bold uppercase', 'text-center')}>
                        {item.name}
                      </div>
                    </Comp>
                  </SheetClose>
                );
              })}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
